/**
 * Fetch wrapper for the Worker's /api/* endpoints.
 *
 * The Worker reads the session from an HttpOnly cookie, which the browser
 * attaches automatically on same-origin requests. There is no Bearer token to
 * manage — `credentials: 'same-origin'` (the default for same-origin URLs)
 * is sufficient.
 */
export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  const resp = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  const text = await resp.text();
  const body = text ? safeJson(text) : null;

  if (!resp.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body && typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : `Request failed (${resp.status})`;
    throw new ApiError(message, resp.status);
  }

  return body as T;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
